import React from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  List,
  Avatar,
  Tag,
  Space,
  Typography,
  Alert,
  Timeline,
  Modal
} from 'antd'
import {
  DollarOutlined,
  TruckOutlined,
  CalculatorOutlined,
  AuditOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  BarChartOutlined,
  EyeOutlined,
  FileTextOutlined,
  UploadOutlined,
  LogoutOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { formatReceiptStatus, getInconsistencyLabel, getReceiptMismatchLabel } from './bossUtils'
import { GlobalStats, RoleProgress, DataComparison, ReceiptCheckSummary, RecentActivity } from './bossTypes'

const { Text, Title } = Typography

interface BossHeaderCardProps {
  user: { nickName?: string; nickname?: string; username?: string; info?: string } | null
  onExport: () => void
  onLogout: () => void
}

export const BossHeaderCard: React.FC<BossHeaderCardProps> = ({ user, onExport, onLogout }) => (
  <Card style={{ marginBottom: 24 }}>
    <Row justify="space-between" align="middle">
      <Col>
        <Space size="large">
          <Avatar size={64} style={{ backgroundColor: '#722ed1' }}>{'\u603b\u7ba1'}</Avatar>
          <div>
            <Title level={4} style={{ margin: 0 }}>{(user as any)?.nickname || user?.nickName || user?.username || '-'}</Title>
            <Text type="secondary">{user?.info || '\u603b\u7ba1\u7406\u5458'}</Text>
          </div>
        </Space>
      </Col>
      <Col>
        <Space>
          <Button icon={<ExportOutlined />} onClick={onExport}>
            {'\u5bfc\u51fa\u6570\u636e'}
          </Button>
          <Button icon={<LogoutOutlined />} onClick={onLogout}>
            {'\u9000\u51fa\u767b\u5f55'}
          </Button>
        </Space>
      </Col>
    </Row>
  </Card>
)

export const BossGlobalStats: React.FC<{ stats: GlobalStats }> = ({ stats }) => (
  <Row gutter={16} style={{ marginBottom: 24 }}>
    <Col xs={12} sm={6}>
      <Card>
        <Statistic
          title={'\u603b\u8425\u6536'}
          value={stats.totalRevenue}
          valueStyle={{ color: 'var(--positive)' }}
          prefix={<DollarOutlined />}
          formatter={(value) => `￥${Number(value || 0).toFixed(2)}`}
        />
      </Card>
    </Col>
    <Col xs={12} sm={6}>
      <Card>
        <Statistic
          title={'\u5728\u79df\u8f66\u8f86\u5f85\u6536\u91d1\u989d'}
          value={Number(stats.pendingAmount || 0)}
          valueStyle={{ color: 'var(--warning)' }}
          prefix={<ClockCircleOutlined />}
          formatter={(value) => `\u5408\u8ba1 \u00a5${Number(value || 0).toFixed(2)}`}
        />
      </Card>
    </Col>
    <Col xs={12} sm={6}>
      <Card>
        <Statistic
          title={'\u6d3b\u8dc3\u6d41\u7a0b'}
          value={stats.activeWorkflows}
          valueStyle={{ color: 'var(--accent)' }}
          prefix={<BarChartOutlined />}
        />
      </Card>
    </Col>
    <Col xs={12} sm={6}>
      <Card>
        <Statistic
          title={'\u4eca\u65e5\u5b8c\u6210'}
          value={stats.completedToday}
          valueStyle={{ color: '#722ed1' }}
          prefix={<CheckCircleOutlined />}
        />
      </Card>
    </Col>
  </Row>
)

interface BossOverviewTabProps {
  roleProgress: RoleProgress
  onViewRoleDetail: (role: 'vehicle_manager' | 'accountant' | 'cashier') => void
}

export const BossOverviewTab: React.FC<BossOverviewTabProps> = ({ roleProgress, onViewRoleDetail }) => (
  <div>
    <Title level={5} style={{ marginBottom: 16 }}>
      <TeamOutlined /> {'\u5404\u89d2\u8272\u5de5\u4f5c\u6982\u89c8'}
    </Title>

    <Row gutter={16} style={{ marginBottom: 24 }} className="boss-verify-list">
      <Col xs={24} sm={8}>
        <Card
          hoverable
          onClick={() => onViewRoleDetail('vehicle_manager')}
          style={{ borderLeft: '4px solid var(--accent)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <TruckOutlined style={{ fontSize: 24, color: 'var(--accent)', marginRight: 12 }} />
            <div>
              <Text strong>{'\u8f66\u8f86\u7ba1\u7406\u5458'}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{'\u6536\u6b3e\u7ba1\u7406'}</Text>
            </div>
            <Button type="link" size="small" style={{ marginLeft: 'auto' }}>
              {'\u67e5\u770b'}
            </Button>
          </div>
          <Row gutter={8}>
            <Col span={8}>
              <Statistic
                title={'\u5f85\u6536\u6b3e'}
                value={roleProgress.vehicleManager.pending}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={'\u5df2\u6536\u6b3e'}
                value={roleProgress.vehicleManager.completed}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={'\u5df2\u903e\u671f'}
                value={roleProgress.vehicleManager.overdue}
                valueStyle={{ fontSize: 16, color: roleProgress.vehicleManager.overdue > 0 ? '#f5222d' : undefined }}
              />
            </Col>
          </Row>
        </Card>
      </Col>

      <Col xs={24} sm={8}>
        <Card
          hoverable
          onClick={() => onViewRoleDetail('accountant')}
          style={{ borderLeft: '4px solid var(--positive)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <CalculatorOutlined style={{ fontSize: 24, color: 'var(--positive)', marginRight: 12 }} />
            <div>
              <Text strong>{'\u4f1a\u8ba1\u5458'}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{'\u8bb0\u8d26\u7ba1\u7406'}</Text>
            </div>
            <Button type="link" size="small" style={{ marginLeft: 'auto' }}>
              {'\u67e5\u770b'}
            </Button>
          </div>
          <Row gutter={8}>
            <Col span={8}>
              <Statistic
                title={'\u5f85\u505a\u8d26'}
                value={roleProgress.accountant.pending}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={'\u5df2\u5b8c\u6210'}
                value={roleProgress.accountant.completed}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={'\u51ed\u8bc1\u6570'}
                value={roleProgress.accountant.vouchers}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>
      </Col>

      <Col xs={24} sm={8}>
        <Card
          hoverable
          onClick={() => onViewRoleDetail('cashier')}
          style={{ borderLeft: '4px solid var(--warning)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <AuditOutlined style={{ fontSize: 24, color: 'var(--warning)', marginRight: 12 }} />
            <div>
              <Text strong>{'\u51fa\u7eb3\u5458'}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{'\u6536\u6b3e\u6838\u5b9e'}</Text>
            </div>
            <Button type="link" size="small" style={{ marginLeft: 'auto' }}>
              {'\u67e5\u770b'}
            </Button>
          </div>
          <Row gutter={8}>
            <Col span={8}>
              <Statistic
                title={'\u5f85\u786e\u8ba4'}
                value={roleProgress.cashier.pending}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={'\u5df2\u786e\u8ba4'}
                value={roleProgress.cashier.completed}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={'\u5df2\u6838\u5b9e'}
                value={roleProgress.cashier.verified}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  </div>
)

interface BossProgressTabProps {
  recentActivities: RecentActivity[]
  globalStats: GlobalStats
}

export const BossProgressTab: React.FC<BossProgressTabProps> = ({ recentActivities, globalStats }) => (
  <div>
    <Title level={5} style={{ marginBottom: 16 }}>
      <ClockCircleOutlined /> {'\u6700\u65b0\u6d3b\u52a8'}
    </Title>

    <Timeline style={{ marginBottom: 32 }}>
      {recentActivities.map((activity) => (
        <Timeline.Item
          key={activity.id}
          dot={<span style={{ fontSize: 16 }}>{activity.icon}</span>}
        >
          <div>
            <Text strong>{activity.user}</Text>
            <Text type="secondary" style={{ float: 'right' }}>{activity.time}</Text>
          </div>
          <div style={{ marginTop: 4 }}>
            <Text>{activity.description}</Text>
          </div>
        </Timeline.Item>
      ))}
    </Timeline>

    <Title level={5} style={{ marginBottom: 16 }}>
      <BarChartOutlined /> {'\u6d41\u7a0b\u7edf\u8ba1'}
    </Title>

    <Row gutter={16}>
      <Col span={12}>
        <Card>
          <Statistic
            title={'\u8fdb\u884c\u4e2d\u6d41\u7a0b'}
            value={globalStats.activeWorkflows}
            valueStyle={{ color: 'var(--accent)' }}
          />
        </Card>
      </Col>
      <Col span={12}>
        <Card>
          <Statistic
            title={'\u4eca\u65e5\u5b8c\u6210'}
            value={globalStats.completedToday}
            valueStyle={{ color: 'var(--positive)' }}
          />
        </Card>
      </Col>
    </Row>
  </div>
)

interface BossVerificationTabProps {
  dataComparison: DataComparison
  receiptChecks: ReceiptCheckSummary
  onOpenReceiptList: () => void
  onOpenReceiptDetail: (receiptId: string) => void
}

export const BossVerificationTab: React.FC<BossVerificationTabProps> = ({
  dataComparison,
  receiptChecks,
  onOpenReceiptList,
  onOpenReceiptDetail
}) => (
  <div className="boss-verify-tab">
    <Title level={5} style={{ marginBottom: 16 }}>
      <EyeOutlined /> {'\u6570\u636e\u4ea4\u53c9\u9a8c\u8bc1'}
    </Title>

    <Row gutter={16} style={{ marginBottom: 24 }}>
      <Col span={12}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>{'\u8f66\u8f86\u7ba1\u7406\u5458 \u27f7 \u4f1a\u8ba1\u5458'}</Text>
            {dataComparison.vehicleVsAccounting === 'match' ? (
              <Tag color="green">{'\u6570\u636e\u4e00\u81f4'}</Tag>
            ) : (
              <Tag color="orange">{'\u5f85\u505a\u8d26'}</Tag>
            )}
          </div>
          <Text type="secondary">{'\u6536\u6b3e\u786e\u8ba4\u4e0e\u505a\u8d26\u8bb0\u5f55\u5bf9\u6bd4'}</Text>
        </Card>
      </Col>
      <Col span={12}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>{'\u4f1a\u8ba1\u5458 \u27f7 \u51fa\u7eb3\u5458'}</Text>
            {dataComparison.accountingVsCashier === 'match' ? (
              <Tag color="green">{'\u6570\u636e\u4e00\u81f4'}</Tag>
            ) : (
              <Tag color="red">{'\u6570\u636e\u4e0d\u4e00\u81f4'}</Tag>
            )}
          </div>
          <Text type="secondary">{'\u505a\u8d26\u8bb0\u5f55\u4e0e\u6536\u6b3e\u6838\u5b9e\u5bf9\u6bd4'}</Text>
        </Card>
      </Col>
    </Row>

    <Card style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong>{'\u6536\u636e\u4e0e\u51ed\u8bc1\u6838\u5bf9'}</Text>
        <Space>
          <Button type="link" onClick={onOpenReceiptList}>{'\u67e5\u770b\u8be6\u60c5'}</Button>
          {receiptChecks.mismatches.length === 0 ? (
            <Tag color="green">{'\u4e00\u81f4'}</Tag>
          ) : (
            <Tag color="red">{'\u4e0d\u4e00\u81f4'}</Tag>
          )}
        </Space>
      </div>
      <Text type="secondary">
        {'\u6536\u636e\u6570\u91cf'}: {receiptChecks.total} {'\u00b7'} {'\u95ee\u9898'}: {receiptChecks.mismatches.length}
      </Text>
    </Card>

    {receiptChecks.mismatches.length > 0 && (
      <List
        className="boss-verify-list"
        dataSource={receiptChecks.mismatches}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Button key="detail" type="link" onClick={() => onOpenReceiptDetail(item.receiptId)}>
                {'\u67e5\u770b\u8be6\u60c5'}
              </Button>
            ]}
          >
            <List.Item.Meta
              avatar={
                <Avatar style={{
                  backgroundColor: item.severity === 'high' ? '#f5222d' :
                    item.severity === 'medium' ? 'var(--warning)' : 'var(--positive)'
                }}>
                  {item.severity === 'high' ? '\u9ad8' : item.severity === 'medium' ? '\u4e2d' : '\u4f4e'}
                </Avatar>
              }
              title={
                <div className="boss-verify-title">
                  <Text strong>{getReceiptMismatchLabel(item.type)}</Text>
                  <Tag
                    color={
                      item.severity === 'high' ? 'red' :
                        item.severity === 'medium' ? 'orange' : 'green'
                    }
                  >
                    {item.severity === 'high' ? '\u9ad8' : item.severity === 'medium' ? '\u4e2d' : '\u4f4e'}
                  </Tag>
                  <Text type="secondary">{item.receiptId}</Text>
                </div>
              }
              description={`${item.description}${item.plate ? ` / ${item.plate}` : ''}${item.company ? ` / ${item.company}` : ''}`}
            />
          </List.Item>
        )}
      />
    )}

    {dataComparison.inconsistencies.length > 0 && (
      <div>
        <Alert
          message={`${'\u53d1\u73b0'} ${dataComparison.inconsistencies.length} ${'\u9879\u6570\u636e\u4e0d\u4e00\u81f4'}`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <List
          className="boss-verify-list"
          dataSource={dataComparison.inconsistencies}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Avatar style={{
                    backgroundColor: item.severity === 'high' ? '#f5222d' :
                      item.severity === 'medium' ? 'var(--warning)' : 'var(--positive)'
                  }}>
                    {item.severity === 'high' ? '🔴' :
                      item.severity === 'medium' ? '🟡' : '🟢'}
                  </Avatar>
                }
                title={
                  <div className="boss-verify-title">
                    <Text strong>{getInconsistencyLabel(item.type)}</Text>
                    <Tag
                      color={
                        item.severity === 'high' ? 'red' :
                          item.severity === 'medium' ? 'orange' : 'green'
                      }
                    >
                      {item.severity === 'high' ? '\u9ad8' :
                        item.severity === 'medium' ? '\u4e2d' : '\u4f4e'}
                    </Tag>
                  </div>
                }
                description={item.description}
              />
            </List.Item>
          )}
        />
      </div>
    )}

    {dataComparison.inconsistencies.length === 0 && (
      <Alert
        message={'\u6570\u636e\u9a8c\u8bc1\u901a\u8fc7'}
        description={'\u6240\u6709\u89d2\u8272\u95f4\u7684\u6570\u636e\u90fd\u4fdd\u6301\u4e00\u81f4\uff0c\u7cfb\u7edf\u8fd0\u884c\u6b63\u5e38\u3002'}
        type="success"
        showIcon
      />
    )}
  </div>
)

interface BossVouchersTabProps {
  roleProgress: RoleProgress
  allReceipts: any[]
  onOpenReceiptDetail: (receiptId: string) => void
}

export const BossVouchersTab: React.FC<BossVouchersTabProps> = ({ roleProgress, allReceipts, onOpenReceiptDetail }) => (
  <div>
    <Title level={5} style={{ marginBottom: 16 }}>
      <FileTextOutlined /> {'\u51ed\u8bc1\u7ba1\u7406'}
    </Title>

    <Row gutter={16} style={{ marginBottom: 24 }}>
      <Col span={8}>
        <Card>
          <Statistic
            title={'\u603b\u51ed\u8bc1\u6570'}
            value={roleProgress.accountant.vouchers}
            valueStyle={{ color: 'var(--accent)' }}
            prefix={<FileTextOutlined />}
          />
        </Card>
      </Col>
      <Col span={8}>
        <Card>
          <Statistic
            title={'\u4eca\u65e5\u4e0a\u4f20'}
            value={0}
            valueStyle={{ color: 'var(--positive)' }}
            prefix={<UploadOutlined />}
          />
        </Card>
      </Col>
      <Col span={8}>
        <Card>
          <Statistic
            title={'\u5f85\u5ba1\u6838'}
            value={0}
            valueStyle={{ color: 'var(--warning)' }}
            prefix={<ExclamationCircleOutlined />}
          />
        </Card>
      </Col>
    </Row>

    <Card title={'\u51ed\u8bc1\u5217\u8868'} extra={<Button type="link">{'\u67e5\u770b\u5168\u90e8'}</Button>}>
      <List
        className="boss-verify-list"
        dataSource={allReceipts.filter((r) => r.voucherData?.voucherNo || r.status === 'accounted').slice(0, 10)}
        renderItem={(receipt) => (
          <List.Item
            actions={[
              <Button
                key="detail"
                type="link"
                icon={<EyeOutlined />}
                onClick={() => onOpenReceiptDetail(receipt._id || receipt.id)}
              >
                {'\u67e5\u770b\u8be6\u60c5'}
              </Button>
            ]}
          >
            <List.Item.Meta
              avatar={<Avatar icon={<FileTextOutlined />} />}
              title={
                <div className="boss-verify-title">
                  <Text strong>{receipt.plate || receipt.company || receipt.customerName || '-'}</Text>
                  <Tag color="green">{receipt.voucherData?.voucherNo || receipt.voucherNo || '-'}</Tag>
                </div>
              }
              description={
                <div>
                  <Text type="secondary">
                    {'\u91d1\u989d'}: ￥{Number(receipt.amount || 0).toFixed(2)} {'|'} {'\u72b6\u6001'}: {formatReceiptStatus(receipt.status)}
                  </Text>
                  <br />
                  <Text type="secondary">
                    {'\u8bb0\u8d26\u65f6\u95f4'}: {receipt.accountedAt ? dayjs(receipt.accountedAt).format('MM-DD HH:mm') : '\u672a\u77e5'}
                  </Text>
                </div>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  </div>
)

interface BossReceiptDetailModalProps {
  open: boolean
  receiptDetail: any
  onClose: () => void
}

export const BossReceiptDetailModal: React.FC<BossReceiptDetailModalProps> = ({
  open,
  receiptDetail,
  onClose
}) => (
  <Modal
    title={'\u6536\u636e\u8be6\u60c5'}
    open={open}
    onCancel={onClose}
    footer={null}
    width={720}
  >
    {receiptDetail ? (
      <div>
        <div style={{ marginBottom: 8 }}><strong>{'\u6536\u636e\u53f7'}:</strong> {receiptDetail._id || receiptDetail.id}</div>
        <div style={{ marginBottom: 8 }}><strong>{'\u5ba2\u6237'}:</strong> {receiptDetail.company || receiptDetail.customerName || '-'}</div>
        <div style={{ marginBottom: 8 }}><strong>{'\u91d1\u989d'}:</strong> {Number(receiptDetail.amount || 0).toFixed(2)}</div>
        <div style={{ marginBottom: 8 }}><strong>{'\u72b6\u6001'}:</strong> {formatReceiptStatus(receiptDetail.status)}</div>
        <div style={{ marginBottom: 8 }}><strong>{'\u660e\u7ec6'}:</strong></div>
        {(receiptDetail.items || []).map((it: any, idx: number) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
            <span>{it.plate || '-'}</span>
            <span>{Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0).toFixed(2)}</span>
            <span>{it.chargeRangeStart && it.chargeRangeEnd ? `${it.chargeRangeStart}~${it.chargeRangeEnd}` : '-'}</span>
          </div>
        ))}
        {receiptDetail.voucherData && (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}><strong>{'\u51ed\u8bc1\u53f7'}:</strong> {receiptDetail.voucherData.voucherNo}</div>
            <div><strong>{'\u501f\u65b9'}:</strong> {Number(receiptDetail.voucherData.totalDebit || 0).toFixed(2)}</div>
            <div><strong>{'\u8d37\u65b9'}:</strong> {Number(receiptDetail.voucherData.totalCredit || 0).toFixed(2)}</div>
          </div>
        )}
      </div>
    ) : (
      <div>{'\u6682\u65e0\u6536\u636e\u6570\u636e'}</div>
    )}
  </Modal>
)

interface BossReceiptListModalProps {
  open: boolean
  receipts: any[]
  onClose: () => void
  onOpenReceiptDetail: (receiptId: string) => void
}

export const BossReceiptListModal: React.FC<BossReceiptListModalProps> = ({
  open,
  receipts,
  onClose,
  onOpenReceiptDetail
}) => (
  <Modal
    title={'\u6536\u636e\u5217\u8868'}
    open={open}
    onCancel={onClose}
    footer={null}
    width={760}
  >
    <List
      dataSource={receipts}
      renderItem={(receipt) => (
        <List.Item
          actions={[
            <Button
              key="detail"
              type="link"
              onClick={() => onOpenReceiptDetail(receipt._id || receipt.id)}
            >
              {'\u67e5\u770b\u8be6\u60c5'}
            </Button>
          ]}
        >
          <List.Item.Meta
            title={
              <Space>
                <Text strong>{receipt.plate || receipt.company || receipt.customerName || '-'}</Text>
                <Tag>{formatReceiptStatus(receipt.status)}</Tag>
              </Space>
            }
            description={`${'\u91d1\u989d'}: ￥${Number(receipt.amount || 0).toFixed(2)} \u00b7 ${'\u5355\u53f7'}: ${receipt._id || receipt.id}`}
          />
        </List.Item>
      )}
    />
  </Modal>
)
